import React, { useCallback, useMemo, useState } from 'react';
import { DateRange, CalendarDate } from './calendar/calendar.utils';
import { DatePicker, DateRangePicker } from './datePicker';

type DatePickerHook = {
  inputClassName?: string;
  modalInputClassName?: string;
  withoutClear?: boolean;
  withoutInit?: boolean;
  initValue?: CalendarDate | null;
  placeholder?: string;
};

type DatePickerHookResult = {
  selectedDate: CalendarDate | null | undefined;
  datePicker: React.ReactNode;
  modalDatePicker: React.ReactNode;
  applyDPChanges: () => void;
  resetDPChanges: () => void;
};

export const useDatePicker = ({
  inputClassName,
  modalInputClassName,
  withoutClear = false,
  withoutInit = false,
  initValue,
  placeholder,
}: DatePickerHook): DatePickerHookResult => {
  const [selectedDate, setSelectedDate] = useState<CalendarDate | null>(
    initValue || (withoutInit ? null : new CalendarDate()),
  );
  const [selectedDateModal, setSelectedDateModal] =
    useState<CalendarDate | null>(selectedDate);

  const onChange = useCallback((date: CalendarDate | null) => {
    setSelectedDate((prevState) =>
      prevState?.time === date?.time ? prevState : date,
    );
    setSelectedDateModal((prevState) =>
      prevState?.time === date?.time ? prevState : date,
    );
  }, []);

  const onModalChange = useCallback((date: CalendarDate | null) => {
    setSelectedDateModal((prevState) =>
      prevState?.time === date?.time ? prevState : date,
    );
  }, []);

  return {
    selectedDate,
    datePicker: (
      <DatePicker
        selectedDate={selectedDate}
        onChange={onChange}
        inputClassName={inputClassName}
        withoutClear={withoutClear}
        placeholder={placeholder}
      />
    ),
    modalDatePicker: (
      <DatePicker
        selectedDate={selectedDate}
        onChange={onModalChange}
        inputClassName={modalInputClassName}
        withoutClear={withoutClear}
        placeholder={placeholder}
      />
    ),
    applyDPChanges: useCallback(() => {
      setSelectedDate(selectedDateModal);
    }, [selectedDateModal]),
    resetDPChanges: useCallback(() => {
      setSelectedDateModal(withoutClear ? selectedDate : null);
    }, [selectedDate, withoutClear]),
  };
};

type DateRangePickerHook = {
  inputClassName?: string;
  modalInputClassName?: string;
  withoutClear?: boolean;
  withoutInit?: boolean;
  initValue?: DateRange;
  placeholder?: string;
  disabled?: boolean | null;
};

type DateRangePickerHookResult = {
  range: DateRange;
  initRange: DateRange;
  dateRangePicker: React.ReactNode;
  modalDateRangePicker: React.ReactNode;
  applyDRPChanges: () => void;
  resetDRPChanges: () => void;
  resetDRPToInit: () => void;
  clearDRP: () => void;
};

export const useDateRangePicker = ({
  inputClassName,
  modalInputClassName,
  withoutClear = false,
  withoutInit = false,
  initValue,
  placeholder,
  disabled = false,
}: DateRangePickerHook): DateRangePickerHookResult => {
  const initRange = useMemo(
    () =>
      initValue ||
      (withoutInit
        ? { start: null, end: null }
        : {
            start: new CalendarDate().setFirstDay(),
            end: new CalendarDate().setLastDay(),
          }),
    [initValue, withoutInit],
  );

  const [range, setRange] = useState<DateRange>(initRange);
  const [rangeModal, setRangeModal] = useState<DateRange>({
    start: range.start ? new CalendarDate(range.start.date) : null,
    end: range.end ? new CalendarDate(range.end.date) : null,
  });

  const onChange = useCallback((value: DateRange) => {
    setRange((prevState) =>
      prevState.start?.time === value.start?.time &&
      prevState.end?.time === value.end?.time
        ? prevState
        : value,
    );
    setRangeModal((prevState) =>
      prevState.start?.time === value.start?.time &&
      prevState.end?.time === value.end?.time
        ? prevState
        : value,
    );
  }, []);

  const onModalChange = useCallback((value: DateRange) => {
    setRangeModal((prevState) =>
      prevState.start?.time === value.start?.time &&
      prevState.end?.time === value.end?.time
        ? prevState
        : value,
    );
  }, []);

  return {
    range,
    initRange,
    dateRangePicker: (
      <DateRangePicker
        range={range}
        onChange={onChange}
        inputClassName={inputClassName}
        withoutClear={withoutClear}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    modalDateRangePicker: (
      <DateRangePicker
        range={rangeModal}
        onChange={onModalChange}
        inputClassName={modalInputClassName}
        withoutClear={withoutClear}
        placeholder={placeholder}
        disabled={disabled}
        size="small"
      />
    ),
    applyDRPChanges: useCallback(() => {
      setRange(rangeModal);
    }, [rangeModal]),
    resetDRPChanges: useCallback(() => {
      setRangeModal(withoutClear ? range : { start: null, end: null });
    }, [range, withoutClear]),
    resetDRPToInit: useCallback(() => {
      setRange(initRange);
      setRangeModal(initRange);
    }, [initRange]),
    clearDRP: useCallback(() => {
      setRange({ start: null, end: null });
      setRangeModal({ start: null, end: null });
    }, []),
  };
};
